// EP-161 §2.2 (D3): the ML reading of a slip, shown beside it for reference
// only. A person verifies every parking slip; nothing here decides anything.
// Reused by the sessions page's expanded row and by the Verify / Reject
// modals. It reads gate_evidence, fraud_suspect and readings.ml, never
// ml_match: the parking settlement writes no ml_match at all (§10 item 1).
//
// The field list is read-only. SlipFieldChecklist is not reused here because
// its rows need a full Payment and carry the correction inputs, which belong
// to the payment detail page.
import { useTranslation } from 'react-i18next'
import { SlipGateChips, FraudSuspectBanner } from '@/components/payments/SlipGateChips'
import type { GateEvidence, SlipReadingOpinion } from '@/types'

export interface SlipReference {
  gateEvidence?: GateEvidence | null
  fraudSuspect?: boolean
  reading?: SlipReadingOpinion | null
}

const FIELD_ROWS = [
  { key: 'amount', label: 'verification.field_amount' },
  { key: 'datetime', label: 'verification.field_datetime' },
  { key: 'sender_name', label: 'verification.field_sender' },
  { key: 'receiver_name', label: 'verification.field_receiver' },
  { key: 'trans_ref', label: 'verification.field_trans_ref' },
] as const

export function SlipReferenceBlock({ gateEvidence, fraudSuspect, reading }: SlipReference) {
  const { t } = useTranslation('payments')
  const hasReading = !!gateEvidence && Object.keys(gateEvidence).length > 0

  const fields = reading?.fields ?? {}
  const rows: { key: string; label: string; value: string }[] = []
  if (reading?.bank_code) {
    rows.push({ key: 'bank_code', label: t('verification.field_bank'), value: t(`verification.banks.${reading.bank_code}`) })
  }
  for (const row of FIELD_ROWS) {
    const value = fields[row.key]?.value
    if (value) rows.push({ key: row.key, label: t(row.label), value })
  }

  return (
    <section className="space-y-2" aria-label={t('verification.reference_heading')}>
      <h4 className="text-sm font-semibold">{t('verification.reference_heading')}</h4>
      <FraudSuspectBanner fraudSuspect={fraudSuspect} />
      {hasReading ? (
        <>
          <dl>
            <SlipGateChips gateEvidence={gateEvidence ?? undefined} />
          </dl>
          {rows.length > 0 && (
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm">
              {rows.map((row) => (
                <div key={row.key} className="contents">
                  <dt className="text-base-content/60">{row.label}</dt>
                  <dd className="font-mono break-all">{row.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </>
      ) : (
        <p className="text-sm text-base-content/60">{t('verification.no_reading')}</p>
      )}
    </section>
  )
}
